import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const darkColors = {
  background: "#121212",
  paper: "#1e1e1e",
  text: "#ffffff",
  textSecondary: "#b0b0b0",
  primary: "#90caf9",
  border: "#333333",
};

const lightColors = {
  background: "#ffffff",
  paper: "#f8f9fa",
  text: "#212529",
  textSecondary: "#6c757d",
  primary: "#1976d2",
  border: "#dee2e6",
};

// Lot Status -> row background tint (light / dark)
const LOT_STATUS_COLORS = {
  "Fully Vested": { light: "rgba(233,30,99,0.08)", dark: "rgba(233,30,99,0.16)" },
  "Partially Vested": { light: "rgba(33,150,243,0.08)", dark: "rgba(33,150,243,0.16)" },
  Locked: { light: "rgba(158,158,158,0.14)", dark: "rgba(255,255,255,0.06)" },
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return value;
};

const formatDate = (value) => {
  if (!value) return "—";
  // Backend sends "YYYY-MM-DD HH:mm:ss" (space-separated), which Safari's
  // Date parser rejects — normalize to ISO-ish before parsing.
  const parsed = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
};

// ── Vesting dialog (error_data === 2) ──────────────────────────────────────
const VestingDialog = ({ errorObj, onClose, colors, isMobile }) => {
  // errorObj is the full API response object
  const error = errorObj?.error ?? errorObj;
  const vestingReportList = errorObj?.vestingReportList;
  const lockedAmount = errorObj?.lockedAmount;

  const { vestingPeriod, remainingDays } = error;

  // Derive currency from the first word of error_msg (vestingReportList rows
  // don't carry a currency field)
  const currency = error?.error_msg?.split(" ")[0] ?? "";

  const showVestingPeriod = vestingPeriod > 0;
  const showRemainingDays = remainingDays > 0;
  const showLockedAmount = lockedAmount != null && lockedAmount !== "" && lockedAmount > 0;
  const showVestingReport = vestingReportList && vestingReportList.length > 0;

  // vestingReportList is already one row per vesting tranche; purchasedAmount
  // repeats per lot (same purchaseTxnRef), so dedupe by lot when summing it.
  const seenLots = new Set();
  let totalPurchased = 0;
  let totalUnlocked = 0;
  let totalLocked = 0;
  if (showVestingReport) {
    vestingReportList.forEach((item) => {
      if (item.purchaseTxnRef && !seenLots.has(item.purchaseTxnRef)) {
        seenLots.add(item.purchaseTxnRef);
        totalPurchased += Number(item.purchasedAmount) || 0;
      }
      totalUnlocked += Number(item.unlockedAmount) || 0;
      totalLocked += Number(item.lockedAmount) || 0;
    });
  }
  const totalAvailable = totalUnlocked;

  const tableCellSx = {
    color: colors.text,
    borderColor: colors.border,
    fontSize: 12,
    whiteSpace: "nowrap",
    px: 1,
    py: 0.75,
  };

  const tableHeadCellSx = {
    ...tableCellSx,
    fontWeight: 700,
    color: colors.textSecondary,
    textTransform: "uppercase",
    fontSize: 9.5,
    letterSpacing: "0.05em",
    bgcolor: colors.background === "#121212" ? "#1a1a1a" : "#f0f0f0",
  };

  return (
    <>
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: `1px solid ${colors.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <LockIcon sx={{ fontSize: 20, color: "#f59e0b" }} />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: colors.text,
              fontSize: isMobile ? "1rem" : "1.2rem",
              lineHeight: 1.3,
            }}
          >
            {currency && (
              <Box
                component="code"
                sx={{
                  fontFamily: "monospace",
                  fontSize: isMobile ? "0.9rem" : "1rem",
                  bgcolor: "rgba(245,158,11,0.15)",
                  color: "#f59e0b",
                  px: "6px",
                  py: "1px",
                  borderRadius: 1,
                  mr: 0.75,
                }}
              >
                {currency}
              </Box>
            )}
            Token Trading Restriction Schedule
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: colors.textSecondary,
            ml: 1,
            flexShrink: 0,
            "&:hover": {
              backgroundColor: colors.background === "#121212" ? "#333" : "#f5f5f5",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Description from API */}
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <Typography
            variant="body1"
            sx={{
              color: colors.textSecondary,
              lineHeight: 1.7,
              fontSize: isMobile ? "0.9rem" : "1rem",
              textAlign: "left",
            }}
          >
            {error?.error_msg}
          </Typography>
        </Box>

        {/* Stat rows */}
        {(showLockedAmount || showVestingPeriod || showRemainingDays) && (
          <Box sx={{ px: 3, pb: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>

            {/* Locked Amount */}
            {showLockedAmount && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  bgcolor: "rgba(245,158,11,0.1)",
                  border: "1px solid rgba(245,158,11,0.3)",
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    bgcolor: "rgba(245,158,11,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <LockIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                </Box>
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: "#f59e0b",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      mb: 0.3,
                    }}
                  >
                    Locked
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#f59e0b" }}>
                    {lockedAmount} tokens
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Unlocks in (remainingDays) */}
            {showRemainingDays && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  bgcolor: "rgba(59,130,246,0.1)",
                  border: "1px solid rgba(59,130,246,0.3)",
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    bgcolor: "rgba(59,130,246,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
                </Box>
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: "#3b82f6",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      mb: 0.3,
                    }}
                  >
                    Unlocks in
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#3b82f6" }}>
                    {remainingDays} days remaining
                  </Typography>
                </Box>
              </Box>
            )} 

            {/* Vesting Period */}
            {showVestingPeriod && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  bgcolor: "rgba(34,197,94,0.1)",
                  border: "1px solid rgba(34,197,94,0.3)",
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    bgcolor: "rgba(34,197,94,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <LockOpenIcon sx={{ fontSize: 16, color: "#22c55e" }} />
                </Box>
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: "#22c55e",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      mb: 0.3,
                    }}
                  >
                    Vesting Period
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#22c55e" }}>
                    {vestingPeriod} months
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Vesting Report Table */}
        {showVestingReport && (
          <Box
            sx={{
              px: { xs: 2, md: 1.5 },
              pb: 3,
              borderTop: `1px solid ${colors.border}`,
              pt: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: colors.textSecondary,
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                mb: 0.75,
              }}
            >
              Trading Restriction Schedule
            </Typography>
            <Typography sx={{ color: colors.text, fontSize: 12.5, fontWeight: 500, mb: 1.5 }}>
              Total Purchased: {formatNumber(totalPurchased)} &nbsp;|&nbsp; Unlocked:{" "}
              {formatNumber(totalUnlocked)} &nbsp;|&nbsp; Locked: {formatNumber(totalLocked)}{" "}
              &nbsp;|&nbsp; Available: {formatNumber(totalAvailable)}
            </Typography>
            <TableContainer
              sx={{
                borderRadius: 2,
                border: `1px solid ${colors.border}`,
                overflowX: "auto",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadCellSx}>Purchase Txn Ref#</TableCell>
                    <TableCell sx={tableHeadCellSx}>Purchase Date</TableCell>
                    <TableCell sx={tableHeadCellSx} align="right">Purchased Amount</TableCell>
                    <TableCell sx={tableHeadCellSx} align="right">Restriction %</TableCell>
                    <TableCell sx={tableHeadCellSx} align="right">Restriction Amount</TableCell>
                    <TableCell sx={tableHeadCellSx}>Restriction Period</TableCell>
                    <TableCell sx={tableHeadCellSx}>Restriction End Date</TableCell>
                    <TableCell sx={tableHeadCellSx}>Restriction Line Status</TableCell>
                    <TableCell sx={tableHeadCellSx} align="right">Unlocked Amount</TableCell>
                    <TableCell sx={tableHeadCellSx} align="right">Locked Amount</TableCell>
                    <TableCell sx={tableHeadCellSx} align="right">Days to Unlock</TableCell>
                    <TableCell sx={tableHeadCellSx}>Lot Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vestingReportList.map((row, idx) => {
                    const tint = LOT_STATUS_COLORS[row.lotStatus];
                    const bgcolor = tint
                      ? colors.background === "#121212"
                        ? tint.dark
                        : tint.light
                      : "transparent";

                    return (
                      <TableRow
                        key={`${row.purchaseTxnRef ?? "lot"}-${idx}`}
                        sx={{ backgroundColor: bgcolor, "&:last-child td": { border: 0 } }}
                      >
                        <TableCell sx={tableCellSx}>{row.purchaseTxnRef ?? "—"}</TableCell>
                        <TableCell sx={tableCellSx}>{formatDate(row.purchaseDate)}</TableCell>
                        <TableCell sx={tableCellSx} align="right">
                          {formatNumber(row.purchasedAmount)}
                        </TableCell>
                        <TableCell sx={tableCellSx} align="right">
                          {row.vestingPercentage == null ? "—" : `${row.vestingPercentage}%`}
                        </TableCell>
                        <TableCell sx={tableCellSx} align="right">
                          {formatNumber(row.vestingAmount)}
                        </TableCell>
                        <TableCell sx={tableCellSx}>{row.vestingPeriod ?? "—"}</TableCell>
                        <TableCell sx={tableCellSx}>{formatDate(row.vestingEndDate)}</TableCell>
                        <TableCell sx={tableCellSx}>{row.status ?? "—"}</TableCell>
                        <TableCell sx={tableCellSx} align="right">
                          {formatNumber(row.unlockedAmount)}
                        </TableCell>
                        <TableCell sx={tableCellSx} align="right">
                          {formatNumber(row.lockedAmount)}
                        </TableCell>
                        <TableCell sx={tableCellSx} align="right">
                          {row.daysToUnlock ?? 0}
                        </TableCell>
                        <TableCell sx={tableCellSx}>{row.lotStatus ?? "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Available balance footer note */}
            <Typography
              variant="body2"
              sx={{ color: colors.textSecondary, fontSize: 12, lineHeight: 1.7, textAlign: "left", mt: 1.5 }}
            >
              You can sell or withdraw{" "}
              <Box component="span" sx={{ color: "#22c55e", fontWeight: 600 }}>
                {totalAvailable} tokens
              </Box>{" "}
              that are already vested and available.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 2,
          borderTop: `1px solid ${colors.border}`,
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          disableElevation
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            textTransform: "none",
            fontWeight: 500,
            fontSize: 14,
            backgroundColor: colors.primary,
            "&:hover": {
              backgroundColor: colors.background === "#121212" ? "#1565c0" : "#115293",
            },
            width: isMobile ? "100%" : "auto",
          }}
        >
          I understand
        </Button>
      </DialogActions>
    </>
  );
};

// ── Simple error dialog (error_data === 1 or fallback) ─────────────────────
const SimpleErrorDialog = ({ message, onClose, colors, isMobile }) => (
  <>
    <DialogTitle
      sx={{
        pb: 2,
        borderBottom: `1px solid ${colors.border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pr: 2,
        position: "relative",
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: colors.text,
          fontSize: isMobile ? "1.25rem" : "1.5rem",
        }}
      >
        Notice
      </Typography>
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          color: colors.textSecondary,
          position: "absolute",
          right: 16,
          "&:hover": {
            backgroundColor: colors.background === "#121212" ? "#333" : "#f5f5f5",
          },
        }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>

    <DialogContent sx={{ py: 4 }}>
      <Typography
        variant="body1"
        sx={{
          color: colors.textSecondary,
          lineHeight: 1.6,
          fontSize: isMobile ? "0.9rem" : "1rem",
          textAlign: "center",
        }}
      >
        {message}
      </Typography>
    </DialogContent>

    <DialogActions
      sx={{
        px: 3,
        pb: 3,
        pt: 2,
        borderTop: `1px solid ${colors.border}`,
        justifyContent: "center",
      }}
    >
      <Button
        onClick={onClose}
        variant="contained"
        disableElevation
        sx={{
          borderRadius: 2,
          px: 4,
          py: 1,
          textTransform: "none",
          fontWeight: 500,
          fontSize: 14,
          backgroundColor: colors.primary,
          "&:hover": {
            backgroundColor: colors.background === "#121212" ? "#1565c0" : "#115293",
          },
          width: isMobile ? "100%" : "auto",
        }}
      >
        Close
      </Button>
    </DialogActions>
  </>
);

// ── Root ErrorDialog ────────────────────────────────────────────────────────
const ErrorDialog = () => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const [open, setOpen] = useState(false);
  const [errorObj, setErrorObj] = useState(null);

  const theme = localStorage.getItem("theme") || "light";
  const colors = theme === "dark" ? darkColors : lightColors;

  useEffect(() => {
    const handler = (e) => {
      setErrorObj(e.detail);
      setOpen(true);
    };
    window.addEventListener("show-error-dialog", handler);
    return () => window.removeEventListener("show-error-dialog", handler);
  }, []);

  const handleClose = () => {
    setOpen(false);
    setErrorObj(null);
  };

  // Support both full result object (error.error_data) and plain error object (error_data)
  const isVesting =
    (errorObj?.error?.error_data ?? errorObj?.error_data) === 2;

  // For SimpleErrorDialog, extract message from either shape
  const simpleMessage =
    errorObj?.error?.error_msg ?? errorObj?.error_msg ?? "";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={isVesting ? "xl" : "sm"}
      fullWidth
      fullScreen={isVesting || isMobile}
      PaperProps={{
        sx: {
          borderRadius: isVesting || isMobile ? 0 : 3,
          backgroundColor: colors.background,
          color: colors.text,
          boxShadow:
            theme === "dark"
              ? "0 8px 32px rgba(0,0,0,0.3)"
              : "0 8px 32px rgba(0,0,0,0.1)",
          overflow: "hidden",
        },
      }}
    >
      {errorObj && (
        isVesting ? (
          <VestingDialog
            errorObj={errorObj}
            onClose={handleClose}
            colors={colors}
            isMobile={isMobile}
          />
        ) : (
          <SimpleErrorDialog
            message={simpleMessage}
            onClose={handleClose}
            colors={colors}
            isMobile={isMobile}
          />
        )
      )}
    </Dialog>
  );
};

export default ErrorDialog;
